import { useEffect } from "react";
import { navigate } from "@reach/router";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { useEvaluation } from "../../hooks/useEvaluation";

interface EvaluationPanelProps {
	rfxId: number;
	rfxSubmissionId?: number;
	isClientView: boolean;
}

export const EvaluationPanel: React.FC<EvaluationPanelProps> = ({
	rfxId,
	rfxSubmissionId,
	isClientView,
}) => {
	const { clientPaths } = useAuthContext();

	const {
		hasStarted,
		isLoading,
		evaluationState,
		fetchEvaluationState,
		startEvaluation,
	} = useEvaluation({ rfxId, rfxSubmissionId });

	useEffect(() => {
		fetchEvaluationState();
	}, [fetchEvaluationState]);

	const handleCreate = async (): Promise<void> => {
		const state = await startEvaluation();
		const evaluationId = state?.id;
		if (evaluationId) {
			navigate(clientPaths.viewEvaluation(rfxId, evaluationId));
		}
	};

	const handleView = (): void => {
		const evaluationId = evaluationState?.id;
		if (evaluationId) {
			navigate(clientPaths.viewEvaluation(rfxId, evaluationId));
		}
	};

	if (!isClientView) {
		return null;
	}

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="48px"
					className="!rounded-lg"
				/>
				<SkeletonBase
					startColor="lightgray"
					width="60%"
					height="40px"
					className="!rounded-lg"
				/>
			</div>
		);
	}

	return (
		<div data-testid="evaluation-panel">
			<div className="mb-6">
				<h2 className="text-xl font-semibold text-gray-900">
					EvaluA<span className="text-utility-brand-700">I</span>tion tool
				</h2>
				<p className="mt-1 text-sm text-gray-500">
					Use AI-assisted evaluation to systematically score and compare supplier
					submissions.
				</p>
			</div>
			<div className="flex items-center gap-4">
				{hasStarted ? (
					<Button
						btntype="primary"
						onClick={handleView}
						data-testid="view-evaluation-btn"
					>
						View Evaluation
					</Button>
				) : (
					<Button
						btntype="primary"
						onClick={handleCreate}
						data-testid="create-evaluation-btn"
					>
						Create Evaluation
					</Button>
				)}
			</div>
		</div>
	);
};
